import React, { useEffect, useMemo, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import Sidebar from "../components/Sidebar";
import Topbar from "../components/Topbar";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faChartPie,
  faChartBar,
  faUsers,
  faListAlt,
  faTicketAlt,
} from "@fortawesome/free-solid-svg-icons";

/* ----------------------- tiny helpers ----------------------- */
const readUser = () => {
  try {
    const u = JSON.parse(localStorage.getItem("user") || "{}");
    return { SLIITID: "0", name: "Unknown User", role: "Member", image: "/user.png", ...u };
  } catch {
    return { SLIITID: "0", name: "Unknown User", role: "Member", image: "/user.png" };
  }
};

const toArray = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.data)) return data.data;      // common wrapper
  if (Array.isArray(data?.items)) return data.items;    // common wrapper
  if (Array.isArray(data?.tickets)) return data.tickets;
  if (Array.isArray(data?.members)) return data.members;
  return [];
};

const safeDate = (v) => {
  const d = new Date(v);
  return Number.isFinite(d.valueOf()) ? d : null;
};

/* ============================================================
   Tickets — Admin List
============================================================ */
function TicketListAdmin() {
  const user = readUser();
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [modal, setModal] = useState({
    open: false,
    ticket: null,
    review: "",
    status: "Review",
    saving: false,
  });

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await fetch("https://dev.sliitleo.org/Backend/tickets_admin.php?mode=get");
      // some hosts return text on errors; try to parse safely
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = [];
      }
      const list = toArray(data).map((t) => ({
        id: t?.id ?? t?.ticket_id ?? "",
        subject: t?.subject ?? "(No subject)",
        user_name: t?.user_name ?? "",
        user_id: t?.user_id ?? "",
        created_at: t?.created_at ?? t?.createdAt ?? "",
        description: t?.description ?? "",
        review: t?.review ?? "",
        status: t?.status ?? "Review",
      }));
      setTickets(list);
    } catch (e) {
      setError("Failed to load tickets");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchTickets();
  }, [fetchTickets]);

  const openReview = (ticket) => {
    setModal({
      open: true,
      ticket,
      review: ticket?.review || "",
      status: ticket?.status || "Review",
      saving: false,
    });
  };

  const submitReview = async (e) => {
    e.preventDefault();
    if (!modal.ticket || !modal.ticket.id) {
      alert("Error: Invalid ticket data. Please try again.");
      return;
    }
    const payload = {
      id: parseInt(String(modal.ticket.id), 10) || modal.ticket.id,
      status: modal.status || "Review",
      review: modal.review || "",
      review_id: user?.SLIITID || "0",
      review_name: user?.name || "System",
    };

    try {
      setModal((m) => ({ ...m, saving: true }));
      const res = await fetch("https://dev.sliitleo.org/Backend/tickets_admin.php?mode=review", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify(payload),
      });
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch {
        data = {};
      }
      if (!res.ok) {
        throw new Error(data?.error || `HTTP ${res.status}`);
      }
      // success
      setModal({ open: false, ticket: null, review: "", status: "Review", saving: false });
      fetchTickets();
    } catch (err) {
      console.error("Error updating review:", err);
      alert(`Failed to update ticket review: ${err?.message || "Unknown error"}`);
      setModal((m) => ({ ...m, saving: false }));
    }
  };

  if (loading) return <div className="text-pink-400">Loading tickets...</div>;
  if (error) return <div className="text-red-400">{error}</div>;
  if (!tickets.length) return <div className="text-gray-400">No tickets found.</div>;

  return (
    <>
      <div className="grid gap-3 sm:gap-4 w-full">
        {tickets.map((ticket) => (
          <div
            key={String(ticket.id)}
            className="flex flex-col sm:flex-row bg-[#181b20] border border-gray-700 rounded-2xl shadow-lg p-3 sm:p-4 md:p-5 gap-3 sm:gap-4 items-start relative cursor-pointer hover:border-pink-500 transition"
            onClick={() => openReview(ticket)}
          >
            <div className="flex-shrink-0 w-10 h-10 sm:w-12 sm:h-12 md:w-14 md:h-14 rounded-full bg-pink-700 flex items-center justify-center text-xl sm:text-2xl font-bold text-white shadow-lg">
              {(ticket.user_name?.[0]?.toUpperCase() ||
                String(ticket.user_id || "U")[0] ||
                "U")}
            </div>

            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 sm:gap-3 mb-2">
                <span className="font-bold text-base sm:text-lg text-white truncate">
                  {ticket.subject}
                </span>
                <span className="text-xs text-gray-400 font-mono shrink-0">#{ticket.id}</span>
                <span
                  className={`ml-auto px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-bold whitespace-nowrap ${
                    ticket.status === "Open"
                      ? "bg-pink-700 text-pink-200"
                      : ticket.status === "Pending"
                      ? "bg-yellow-700 text-yellow-200"
                      : ticket.status === "Review"
                      ? "bg-blue-700 text-blue-200"
                      : "bg-green-700 text-green-200"
                  }`}
                >
                  {ticket.status}
                </span>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs sm:text-sm mt-2">
                <div className="truncate">
                  <span className="text-gray-400">User:</span>
                  <span className="text-white font-semibold ml-2">
                    {ticket.user_name || ticket.user_id || "—"}
                  </span>
                </div>
                <div className="truncate">
                  <span className="text-gray-400">Created:</span>
                  <span className="text-white ml-2">{ticket.created_at || "—"}</span>
                </div>
                <div className="md:col-span-2">
                  <span className="text-gray-400">Description:</span>
                  <span className="text-white ml-2">{ticket.description || "—"}</span>
                </div>
                <div className="md:col-span-2">
                  <span className="text-gray-400">Review:</span>
                  <span className="text-pink-300 ml-2">
                    {ticket.review && String(ticket.review).trim().length
                      ? ticket.review
                      : "No review"}
                  </span>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {modal.open && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black/80 p-2 sm:p-4">
          <form
            className="bg-[#181b20] border border-pink-400/30 rounded-2xl shadow-2xl p-4 sm:p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto relative"
            onSubmit={submitReview}
          >
            <button
              type="button"
              className="absolute top-3 right-4 text-gray-400 hover:text-pink-400 text-2xl font-bold"
              onClick={() =>
                setModal({ open: false, ticket: null, review: "", status: "Review", saving: false })
              }
            >
              &times;
            </button>

            <h3 className="text-lg sm:text-xl font-bold text-white mb-4">Review Ticket</h3>

            <div className="mb-4">
              <label className="block text-gray-200 text-sm sm:text-base mb-1">
                Review Comments
              </label>
              <textarea
                className="w-full rounded bg-gray-800 border border-gray-700 p-3 text-sm sm:text-base min-h-[120px]"
                rows={4}
                value={modal.review}
                onChange={(e) => setModal((m) => ({ ...m, review: e.target.value }))}
              />
            </div>

            <div className="mb-4">
              <label className="block text-gray-200 mb-1">Status</label>
              <select
                className="w-full rounded bg-gray-800 border border-gray-700 text-white p-3 text-sm sm:text-base"
                value={modal.status}
                onChange={(e) => setModal((m) => ({ ...m, status: e.target.value }))}
              >
                <option value="Pending">Pending</option>
                <option value="Review">Review</option>
                <option value="Closed">Closed</option>
              </select>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 justify-end mt-4">
              <button
                type="button"
                className="px-4 py-2 rounded-xl bg-gray-700/80 text-gray-200 font-medium hover:bg-gray-600/90 transition"
                onClick={() =>
                  setModal({ open: false, ticket: null, review: "", status: "Review", saving: false })
                }
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-4 py-2 rounded-xl bg-gradient-to-r from-pink-500 to-pink-700 text-white font-medium shadow-lg hover:from-pink-600 hover:to-pink-800 focus:outline-none focus:ring-2 focus:ring-pink-400 transition"
                disabled={modal.saving}
              >
                {modal.saving ? "Saving..." : "Submit Review"}
              </button>
            </div>
          </form>
        </div>
      )}
    </>
  );
}

/* ============================================================
   Page Shell
============================================================ */
export default function ClubStatistics() {
  const user = readUser();
  const [members, setMembers] = useState([]);
  const [mLoading, setMLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch("https://dev.sliitleo.org/Backend/member_api.php");
        const text = await res.text();
        let data;
        try {
          data = JSON.parse(text);
        } catch {
          data = [];
        }
        setMembers(toArray(data));
      } catch {
        setMembers([]);
      } finally {
        setMLoading(false);
      }
    })();
  }, []);

  // Make sure downstream .filter/.map never crash
  const memberList = useMemo(() => (Array.isArray(members) ? members : []), [members]);

  // Example: compute newcomers safely (won’t crash if API changes)
  const now = new Date();
  const newcomersMonth = memberList.filter((m) => {
    const d = safeDate(m?.RegDate);
    return d && d.getMonth() === now.getMonth() && d.getFullYear() === now.getFullYear();
  }).length;

  // (The counts aren’t displayed here, but leaving the logic shows how to compute without crashes.)

  return (
    <div className="flex bg-[#111827] text-white min-h-screen">
      <Sidebar />
      <Topbar user={user} />
      <main className="md:ml-64 ml-0 mt-14 w-full px-2 sm:px-4 md:px-6 lg:px-8 py-4 md:py-6 lg:py-10">
        <div className="backdrop-blur-md bg-gradient-to-r from-[#23272f]/80 to-[#181b20]/80 border border-gray-800 rounded-2xl p-3 sm:p-4 md:p-5 mb-6 sm:mb-8 flex items-center gap-3 sm:gap-4 shadow-lg">
          <span className="bg-pink-600 p-2 sm:p-3 rounded-full shadow-lg flex-shrink-0">
            <FontAwesomeIcon icon={faTicketAlt} className="text-white text-xl" />
          </span>
          <div className="min-w-0">
            <h1 className="text-base sm:text-lg font-bold tracking-wide text-white truncate">
              Club Dashboard
            </h1>
            <p className="text-sm sm:text-base text-gray-400 truncate">Tickets Admin</p>
          </div>
        </div>

        <div className="mb-8">
          <div className="flex flex-wrap gap-3 mt-4">
            <Link
              to="/club-statistics"
              className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-orange-400 group"
            >
              <span className="text-gray-400 text-lg">
                <FontAwesomeIcon icon={faChartPie} />
              </span>
              Club Statistics
            </Link>

            <Link
              to="/reporting-hub"
              className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-blue-400 group"
            >
              <span className="text-blue-400 text-lg">
                <FontAwesomeIcon icon={faChartBar} />
              </span>
              Reporting Hub
            </Link>

            <Link
              to="/member-database"
              className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-red-400 group"
            >
              <span className="text-red-400 text-lg">
                <FontAwesomeIcon icon={faUsers} />
              </span>
              Member Database
            </Link>

            <Link
              to="/category-selection"
              className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-green-400 group"
            >
              <span className="text-green-400 text-lg">
                <FontAwesomeIcon icon={faListAlt} />
              </span>
              Category Selections
            </Link>

            <Link
              to="/tickets-admin"
              className="flex items-center gap-2 bg-[#23272f] px-6 py-2 rounded-xl text-base font-semibold text-white border border-gray-700 shadow hover:scale-105 transition focus:outline-none focus:ring-2 focus:ring-pink-400 group"
            >
              <span className="text-pink-400 text-lg">
                <FontAwesomeIcon icon={faTicketAlt} />
              </span>
              Tickets Admin
            </Link>
          </div>
        </div>

        <div className="grid gap-6 sm:gap-8 mb-8 sm:mb-10">
          <div className="bg-[#23272f] rounded-2xl p-4 sm:p-6 shadow-xl border border-gray-800">
            <h2 className="text-lg sm:text-xl font-bold text-white mb-3 sm:mb-4">
              All Tickets (Admin View)
            </h2>
            <TicketListAdmin />
          </div>
        </div>
      </main>
    </div>
  );
}
